import React, { useState, useEffect } from 'react';
import { Sec, SH } from '../components/Section';
import { Btn } from '../components/Button';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { CheckIcon, ZapIcon } from '../components/BrandIcons';
import { PaymentCheckout } from '../components/PaymentCheckout';
import { pricingService } from '../services/pricingService';

export const PricingWidget = () => {
  const { theme } = useTheme();
  const { user, setShowAuthModal } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currency, setCurrency] = useState('INR');
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch pricing plans from Supabase
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const data = await pricingService.getPricingPlans();
        setPlans(data);
      } catch (error) {
        console.error('Error fetching pricing plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleSelectPlan = (plan) => {
    if (plan.id === 'free') {
      window.location.hash = '/score';
      return;
    }

    // Open external URL in new tab
    window.open(`${process.env.REACT_APP_DECX_BASE_URL}/registration-screen`, '_blank');
    
    // if (!user) {
    //   if (setShowAuthModal) {
    //     setShowAuthModal(true);
    //   }
    //   return;
    // }
    
    // setSelectedPlan(plan);
    // setShowCheckout(true);
  };

  const handlePaymentSuccess = (data) => {
    console.log('Payment successful:', data);
    setTimeout(() => {
      setShowCheckout(false);
      window.location.hash = '/dashboard';
    }, 2000);
  };

  const formatPrice = (plan) => {
    if (currency === 'INR') {
      const price = billingCycle === 'yearly' ? plan.price_yearly_inr : plan.price_monthly_inr;
      return price === 0 ? '₹0' : `₹${price?.toLocaleString('en-IN') || 0}`;
    } else {
      const price = billingCycle === 'yearly' ? plan.price_yearly_usd : plan.price_monthly_usd;
      return price === 0 ? '$0' : `$${price || 0}`;
    }
  };

  const getOriginalPrice = (plan) => {
    if (!plan.original_price_monthly_inr && !plan.original_price_monthly_usd) return null;
    
    if (currency === 'INR') {
      const price = billingCycle === 'yearly' ? plan.original_price_yearly_inr : plan.original_price_monthly_inr;
      return price ? `₹${price.toLocaleString('en-IN')}` : null;
    } else {
      const price = billingCycle === 'yearly' ? plan.original_price_yearly_usd : plan.original_price_monthly_usd;
      return price ? `$${price}` : null;
    }
  };

  const showTaxNote = (plan) => {
    if (currency === 'INR' && plan.id === 'paid' && plan.price_monthly_inr > 0) {
      return true;
    }
    return false;
  };

  // Loading state
  if (loading) {
    return (
      <Sec id="pricing" dark={theme === 'light'}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: '18px', color: theme === 'dark' ? '#fff' : '#1a1a2e' }}>
            Loading pricing plans...
          </div>
        </div>
      </Sec>
    );
  }

  return (
    <Sec id="pricing" dark={theme === 'light'}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <SH
          eyebrow="PRICING"
          title="Start Free."
          accent="Unlock Full Intelligence."
          subtitle="Choose the depth that fits your needs."
          center
        />

        {/* Currency Toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px', marginBottom: '16px' }}>
          <div
            style={{
              display: 'inline-flex',
              background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              borderRadius: '100px',
              padding: '4px',
              border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
            }}
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrency('INR')}
              style={{
                padding: '10px 24px',
                borderRadius: '100px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: "'Sansation', sans-serif",
                background: currency === 'INR' ? C.grad : 'transparent',
                color: currency === 'INR' ? '#fff' : (theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="currency-inr"
            >
              INR
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrency('USD')}
              style={{
                padding: '10px 24px',
                borderRadius: '100px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: "'Sansation', sans-serif",
                background: currency === 'USD' ? C.grad : 'transparent',
                color: currency === 'USD' ? '#fff' : (theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="currency-usd"
            >
              USD
            </motion.button>
          </div>
        </div>

        {/* Plans Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 380px))',
            justifyContent: 'center',
            gap: '24px',
            marginTop: '32px',
            marginBottom: '48px',
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto',
            padding: '0 20px'
          }}
        >
          {plans.map((p, i) => (
            <motion.div
              key={p.id || i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, scale: 1.02 }}
              style={{ 
                flex: '1',
                minWidth: '280px',
                maxWidth: '400px',
                position: 'relative'
              }}
            >
              {/* Badge on Top Right */}
              {p.badge && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '20px',
                    background: C.grad,
                    color: '#fff',
                    padding: '6px 16px',
                    borderRadius: '100px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 10,
                    fontFamily: "'Sansation', sans-serif"
                  }}
                >
                  {p.badge}
                </div>
              )}

              <div
                style={{
                  background: p.highlight 
                    ? (theme === 'dark' ? `${C.primary}15` : `${C.primary}08`)
                    : (theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.9)'),
                  border: p.highlight 
                    ? `2px solid ${C.secondary}` 
                    : `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  borderRadius: '24px',
                  padding: '32px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: p.highlight 
                    ? C.neon 
                    : (theme === 'dark' ? 'none' : '0 4px 20px rgba(0,0,0,0.08)')
                }}
                data-testid={`pricing-plan-${p.id}`}
              >
                <div style={{ marginBottom: '24px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '12px',
                      marginBottom: '12px'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 700,
                        letterSpacing: '0.15em',
                        color: C.secondary,
                      }}
                    >
                      {p.name}
                    </div>

                    {/* deliveryTime with delivery time */}
                    {p.deliveryTime && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: '#ffffff',
                          padding: '4px 12px',
                          borderRadius: '100px',
                          fontSize: '10px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {p.deliveryTime}
                      </div>
                    )}
                  </div>

                  {/* Price with Cut Price and Tax Note */}
                  <motion.div
                    key={billingCycle + currency}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        fontSize: '40px', 
                        fontWeight: 700, 
                        fontFamily: "'Sansation', sans-serif",
                        color: theme === 'dark' ? '#fff' : '#1a1a2e'
                      }}>
                        {formatPrice(p)}
                      </span>
                      
                      {/* Show cut price if original price exists */}
                      {getOriginalPrice(p) && (
                        <span style={{ 
                          fontSize: '18px', 
                          fontWeight: 500, 
                          fontFamily: "'Sansation', sans-serif",
                          color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                          textDecoration: 'line-through',
                          opacity: 0.7
                        }}>
                          {getOriginalPrice(p)}
                        </span>
                      )}

                      {p.gst && (currency === 'INR') && (
                        <span style={{
                          fontSize: '11px',
                          color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                          fontWeight: 500,
                          opacity: 0.8
                        }}>
                          + {p.gst}% gst
                        </span>
                      )}
                    </div>
                    
                    {/* Show discount badge for paid plan */}
                    {p.id === 'paid' && p.original_price_monthly_inr && (
                      <div style={{ marginTop: '8px' }}>
                        <span style={{
                          background: '#10b981',
                          color: '#fff',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          display: 'inline-block'
                        }}>
                          Save {currency === 'INR' 
                            ? Math.round(((p.original_price_monthly_inr - p.price_monthly_inr) / p.original_price_monthly_inr) * 100)
                            : Math.round(((p.original_price_monthly_usd - p.price_monthly_usd) / p.original_price_monthly_usd) * 100)}%
                        </span>
                      </div>
                    )}
                  </motion.div>
                </div>

                {/* Features - 2 Column Grid */}
                <div style={{ 
                  flex: 1, 
                  marginBottom: '24px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px 16px',
                  fontSize: '12px',
                }}>
                  {/* Regular features (with checkmark) */}
                  {p.features && p.features.map((f, j) => (
                    <div key={`feature-${j}`} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '8px',
                      minWidth: 0,
                      minHeight: '32px'
                    }}>
                      <span style={{ 
                        marginTop: '2px', 
                        flexShrink: 0,
                        color: '#10b981',
                        fontSize: '12px',
                        lineHeight: 1,
                        opacity: 0.9
                      }}>
                        ✓
                      </span>
                      <span className="font-[Manrope]" style={{ 
                        lineHeight: '16px', 
                        opacity: 0.9,
                        color: theme === 'dark' ? '#fff' : '#1a1a2e',
                        wordBreak: 'break-word'
                      }}>
                        {f}
                      </span>
                    </div>
                  ))}
                  
                  {/* Strikethrough features (only for free plan) */}
                  {p.strikethroughFeatures && p.strikethroughFeatures.map((f, j) => (
                    <div key={`strikethrough-${j}`} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '8px',
                      minWidth: 0,
                      opacity: 0.6
                    }}>
                      <span style={{ 
                        marginTop: '2px', 
                        flexShrink: 0,
                        color: '#ef4444',
                        fontSize: '12px'
                      }}>
                        ✕
                      </span>
                      <span className="font-[Manrope]" style={{ 
                        lineHeight: 1.4, 
                        textDecoration: 'line-through',
                        color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                        wordBreak: 'break-word'
                      }}>
                        {f}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleSelectPlan(p)}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: p.highlight ? 'none' : `1px solid ${C.secondary}`,
                    background: p.highlight ? C.grad : (p.id === 'paid' ? C.grad : 'transparent'),
                    color: (p.highlight || p.id === 'paid') ? '#fff' : C.secondary,
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {p.cta}
                </button>

                {p.note && (
                  <div
                    className="font-[Manrope]"
                    style={{
                      marginTop: '12px',
                      fontSize: '11px',
                      opacity: 0.6,
                      textAlign: 'center',
                      color: theme === 'dark' ? '#fff' : '#1a1a2e'
                    }}
                  >
                    {p.note}
                  </div>
                )}

                {p.best && (
                  <div
                    className="font-[Manrope]"
                    style={{
                      marginTop: '12px',
                      fontSize: '11px',
                      opacity: 0.6,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      color: theme === 'dark' ? '#fff' : '#1a1a2e'
                    }}
                  >
                    Best for: {p.best}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Enterprise Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            background: `${C.primary}10`,
            border: `1px solid ${C.primary}40`,
            borderRadius: '24px',
            padding: '48px',
            textAlign: 'center'
          }}
          data-testid="enterprise-section"
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.15em',
              color: C.secondary,
              marginBottom: '16px'
            }}
          >
            ENTERPRISE
          </div>
          <h3 style={{ 
            fontSize: '32px', 
            fontWeight: 700, 
            marginBottom: '16px', 
            fontFamily: "'Sansation', sans-serif",
            color: theme === 'dark' ? '#fff' : '#1a1a2e'
          }}>
            Custom Plan
          </h3>
          <p className="font-[Manrope]" style={{ 
            fontSize: '16px', 
            opacity: 0.8, 
            marginBottom: '32px', 
            maxWidth: '600px', 
            margin: '0 auto 32px',
            color: theme === 'dark' ? '#fff' : '#1a1a2e'
          }}>
            Unlimited credits, team access, white-label reports, API access. Best for accelerators, VC portfolios, large teams.
          </p>
          <Btn variant="primary" to="/contact" large>
            Contact Sales
          </Btn>
        </motion.div>
      </div>

      {/* Payment Checkout Modal */}
      <PaymentCheckout
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        selectedPlan={selectedPlan}
        selectedCreditPack={null}
        billingCycle={billingCycle}
        onSuccess={handlePaymentSuccess}
      />
    </Sec>
  );
};